import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { DropdownProps, DropdownOption } from "./types";
import ChevronDownIcon from "../../assets/svg/chevron-down-icon";
import CheckIcon from "../../assets/svg/check-icon";
import { visibleScrollbar } from "../../utils/functions/get-visible-scrollbar";
import InfoBtnIcon from "../../assets/svg/info-btn-icon";
import { Tooltip } from "../Tooltip/Tooltip";

const Dropdownv2: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  id,
  placeholder = "Select an option",
  disabled = false,
  className = "",
  selectedOptionContainerClassName = "",
  triggerClassName = "",
  triggerIconClassName = "",
  dropdownClassName = "",
  optionClassName = "",
  checkIconClassName = "",
  ariaLabel,
  ariaDescribedBy,
  showChevron = true,
  label,
  labelClassName = "",
  placeholderClassName = "",
  showTooltip = false,
  tooltipText = "",
  dropdownMenuTitle,
  customTriggerStyle,
  showLineSeparator = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [openUpward, setOpenUpward] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  const dropdownId = useMemo(
    () => id || `dropdown-${Math.random().toString(36).slice(2, 9)}`,
    [id]
  );
  const listboxId = `${dropdownId}-listbox`;
  const triggerId = `${dropdownId}-trigger`;
  const defaultAriaDescribedBy = `${dropdownId}-description`;

  const selectedOption = useMemo(
    () => options?.find((option) => option.value === value),
    [options, value]
  );

  // Toggle dropdown open/close
  const handleToggle = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
      setFocusedIndex(-1);
    }
  }, [disabled]);

  // Handle option selection
  const handleOptionSelect = useCallback(
    (option: DropdownOption) => {
      if (!option.disabled) {
        onChange(option.value, option);
        setIsOpen(false);
        setFocusedIndex(-1);
        triggerRef.current?.focus();
      }
    },
    [onChange]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled) return;

      switch (event.key) {
        case "Enter":
        case " ":
          event.preventDefault();
          if (isOpen && focusedIndex >= 0) {
            handleOptionSelect(options[focusedIndex]);
          } else {
            handleToggle();
          }
          break;
        case "Escape":
          if (isOpen) {
            setIsOpen(false);
            setFocusedIndex(-1);
            triggerRef.current?.focus();
          }
          break;
        case "ArrowDown":
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
            setFocusedIndex(0);
          } else {
            setFocusedIndex((prev) => {
              const nextIndex = prev < options?.length - 1 ? prev + 1 : 0;
              for (let i = nextIndex; i < options?.length; i++) {
                if (!options[i].disabled) return i;
              }
              for (let i = 0; i < nextIndex; i++) {
                if (!options[i].disabled) return i;
              }
              return prev;
            });
          }
          break;
        case "ArrowUp":
          event.preventDefault();
          if (isOpen) {
            setFocusedIndex((prev) => {
              const nextIndex = prev > 0 ? prev - 1 : options?.length - 1;
              for (let i = nextIndex; i >= 0; i--) {
                if (!options[i].disabled) return i;
              }
              for (let i = options?.length - 1; i > nextIndex; i--) {
                if (!options[i].disabled) return i;
              }
              return prev;
            });
          }
          break;
        case "Home":
          event.preventDefault();
          if (isOpen) {
            const firstEnabledIndex = options.findIndex(
              (option) => !option.disabled
            );
            if (firstEnabledIndex !== -1) {
              setFocusedIndex(firstEnabledIndex);
            }
          }
          break;
        case "End":
          event.preventDefault();
          if (isOpen) {
            for (let i = options.length - 1; i >= 0; i--) {
              if (!options[i].disabled) {
                setFocusedIndex(i);
                break;
              }
            }
          }
          break;
      }
    },
    [disabled, isOpen, focusedIndex, options, handleToggle, handleOptionSelect]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is outside the entire dropdown container
      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    // Small delay to prevent immediate closing on open click
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Reset focused index when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1);
    }
  }, [isOpen]);

  // Auto-detect whether to open upward or downward
  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menuHeight = listboxRef.current?.offsetHeight ?? 250;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const gap = 4;
    setOpenUpward(spaceBelow < menuHeight + gap && spaceAbove > spaceBelow);
  }, [isOpen]);

  // Render trigger content
  const renderTrigger = () => {
    return (
      <div
        className={`flex items-center gap-[8px] justify-between w-fit px-[8px] py-[7px] ${selectedOptionContainerClassName}`}
      >
        <div
          className={`text-sm font-medium ${
            selectedOption
              ? "text-heading-text dark:text-heading-text-dk"
              : placeholderClassName ||
                "text-heading-text dark:text-heading-text-dk"
          }`}
        >
          {selectedOption
            ? selectedOption?.selectedOptionContent
              ? selectedOption?.selectedOptionContent
              : selectedOption.content
            : placeholder}
        </div>
        {showLineSeparator && (
          <div className="shrink-0 bg-fixed-white dark:bg-fixed-white-dk w-px h-[10px]"></div>
        )}
        {showChevron && (
          <ChevronDownIcon
            className={`w-[8px] h-[8px] shrink-0 text-heading-text dark:text-heading-text-dk transition-transform duration-200 ease-out ${triggerIconClassName} ${
              isOpen ? "rotate-180" : "rotate-0"
            }`}
          />
        )}
      </div>
    );
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label
          htmlFor={triggerId}
          className={`flex items-center gap-[2px] font-normal text-verba-field-label dark:text-verba-field-label-dk mb-1 ${labelClassName}`}
        >
          <div>{label}</div>
          {showTooltip && (
            <Tooltip content={tooltipText}>
              <button type="button">
                <InfoBtnIcon className="text-gray-placeholder dark:text-gray-placeholder-dk w-[18px] h-[18px]" />
              </button>
            </Tooltip>
          )}
        </label>
      )}

      <div
        ref={triggerRef}
        id={triggerId}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-label={ariaLabel || "Select an option"}
        aria-describedby={ariaDescribedBy || defaultAriaDescribedBy}
        tabIndex={disabled ? -1 : 0}
        className={`select-none cursor-pointer rounded-[6px] border border-border-gray dark:border-border-gray-dk bg-popper-bg dark:bg-popper-bg-dk transition-colors focus:outline-none
          ${disabled ? "opacity-60 cursor-not-allowed pointer-events-none" : ""}
          ${triggerClassName}
        `}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        style={customTriggerStyle}
      >
        {renderTrigger()}
      </div>

      {isOpen && (
        <div
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          aria-label="Options"
          aria-activedescendant={
            focusedIndex >= 0
              ? `${dropdownId}-option-${focusedIndex}`
              : undefined
          }
          className={`
            absolute left-0 w-full overflow-y-auto ${visibleScrollbar(
              "[&::-webkit-scrollbar]:w-[8px]"
            )}
            border border-border-gray dark:border-border-gray-dk 
            bg-popper-bg dark:bg-popper-bg-dk
            rounded-[8px] shadow-lg max-h-[250px] z-50
            ${dropdownClassName}
          }`}
          style={{
            ...(openUpward
              ? { bottom: "calc(100% + 4px)" }
              : { top: "calc(100% + 4px)" }),
            transform: "translateZ(0)",
            backfaceVisibility: "hidden",
            WebkitFontSmoothing: "antialiased",
            animation: "dropdownFadeIn 150ms ease-out",
          }}
        >
          {dropdownMenuTitle && <div>{dropdownMenuTitle}</div>}
          {options?.length > 0 ? (
            options.map((option, index) => {
              const isSelected = option.value === value;
              const isFocused = focusedIndex === index;
              const isDisabled = option.disabled || false;

              return (
                <div
                  key={option.id || `${dropdownId}-option-${index}`}
                  id={`${dropdownId}-option-${index}`}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={isDisabled}
                  tabIndex={-1}
                  className={`
                    flex items-center justify-between cursor-pointer text-md
                    dark:text-heading-text-dk
                    last:border-b-0 transition-colors px-[14px] py-[10px] min-w-0
                    ${
                      isDisabled
                        ? "opacity-50 cursor-not-allowed"
                        : isSelected
                        ? "bg-crm-card-highlight-2 dark:bg-crm-card-highlight-2-dk font-medium"
                        : isFocused
                        ? "bg-hover-effect dark:bg-hover-effect-dk"
                        : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                    }
                    ${optionClassName}
                  `}
                  onClick={() => handleOptionSelect(option)}
                  onMouseEnter={() => !isDisabled && setFocusedIndex(index)}
                  onMouseLeave={() => setFocusedIndex(-1)}
                >
                  <div className="overflow-hidden flex-1 min-w-0 pr-5 select-none">
                    {option.content}
                  </div>

                  {isSelected && (
                    <CheckIcon
                      className={`w-4 h-4 text-verba-blue dark:text-verba-blue-dk shrink-0 ${checkIconClassName}`}
                    />
                  )}
                </div>
              );
            })
          ) : (
            <div className="px-3 py-2 text-md text-heading-text dark:text-heading-text-dk">
              No options available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dropdownv2;
